import { useTranslation } from '@/features/i18n';
import { Button } from '@/ui/buttons/Button';
import { Input } from '@/ui/form/Input';
import { Label } from '@/ui/form/Label';
import { logger } from '@/utils/loggerUtils';
import { CardElement } from '@stripe/react-stripe-js';
import { PaymentMethod, Stripe, StripeElements } from '@stripe/stripe-js';
import { useTheme } from 'next-themes';
import { useState } from 'react';
import PoweredByBadge from '../PoweredBy/PoweredByBadge';

type AddPaymentMethodFormOnlyProps = {
  elements: StripeElements | null;
  onSuccess: (paymentMethod: PaymentMethod) => Promise<void>;
  stripe: Stripe | null;
  buttonText?: string;
  onCancel?: () => void;
};

/**
 * A standalone form component for adding a payment method
 * This component only creates the payment method and returns it via onSuccess
 * It does NOT update the customer's default payment method - this is left to the parent component
 */
const AddPaymentMethodFormOnly = ({
  buttonText = 'Submit',
  elements,
  onCancel,
  onSuccess,
  stripe,
}: AddPaymentMethodFormOnlyProps) => {
  const [name, setName] = useState('');
  const { resolvedTheme: theme } = useTheme();
  const { t } = useTranslation('translation');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    try {
      event.preventDefault();

      setSubmitting(true);

      if (!stripe || !elements) {
        logger.error('Stripe.js has not loaded yet, returning');
        return;
      }

      const cardElement = elements.getElement(CardElement)!;

      if (!cardElement) {
        logger.error('Card element not found, returning');
        return;
      }

      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name,
        },
      });

      if (result.error) {
        logger.error(result.error);
        throw new Error(
          'Failed to create payment method: ' + result.error.message,
        );
      }

      const paymentMethod = result.paymentMethod;

      logger.log('Successfully created payment method');
      await onSuccess?.(paymentMethod);
    } catch (error) {
      logger.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col items-start w-full max-w-md mx-auto"
    >
      <div className="w-full bg-background-primary bg-opacity-70 border-background-primary border-2 shadow-contrastShadow rounded-lg p-6 space-y-6">
        <div className="space-y-4">
          <div>
            <Label
              htmlFor="card"
              className="block text-base font-semibold mb-2 text-text-primary"
            >
              {t('subscriptions.card-number')}
            </Label>
            <div className="mt-1 relative rounded-md shadow-sm border border-background-border p-4 bg-background-secondary">
              <CardElement
                options={{
                  hidePostalCode: true,
                  disableLink: true,
                  style: {
                    base: {
                      color:
                        theme === 'dark'
                          ? 'rgb(255, 255, 255)'
                          : 'rgb(0, 0, 0)',
                      fontFamily: 'system-ui, sans-serif',
                      fontSize: '16px',
                      '::placeholder': {
                        color:
                          theme === 'dark'
                            ? 'rgba(156, 163, 175, 0.7)'
                            : 'rgba(115, 115, 115, 0.7)',
                      },
                    },
                  },
                  classes: {
                    focus: 'border-primary',
                  },
                }}
              />
            </div>
          </div>

          {/* Name on card field */}
          <div>
            <Label
              htmlFor="name"
              className="block text-base font-semibold mb-2 text-text-primary"
            >
              {t('subscriptions.cardholder-name')}
            </Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              id="name"
              className="rounded-md shadow-sm border border-background-border bg-background-secondary w-full p-4 text-text-primary"
              placeholder="John Doe"
            />
          </div>
        </div>

        <div className="flex flex-row gap-4 justify-end mt-4">
          {onCancel && (
            <Button
              variant="textColored"
              onClick={onCancel}
              disabled={submitting}
            >
              {t('cancel')}
            </Button>
          )}
          <Button
            variant="primary"
            type="submit"
            disabled={submitting}
            loading={submitting}
            loadingText={t('processing')}
            {...(!onCancel && { className: 'w-full' })}
          >
            {buttonText}
          </Button>
        </div>

        <div className="mt-4 flex flex-col items-center space-y-3">
          <div className="text-xs text-text-tertiary">
            {t('subscriptions.secure-payment-processing')}
          </div>
          <PoweredByBadge source="stripe" />
        </div>
      </div>
    </form>
  );
};

export default AddPaymentMethodFormOnly;
